/**
 * File: stock-data.service.ts
 * Project: portfolio
 * File Created: Monday, 5th February 2018 9:27:35 am
 * Author: Thibaut Jacob (thibautquentinjacob@gmail.com)
 * -----
 * Last Modified: Thursday, 22nd February 2018 3:21:41 pm
 * Modified By: Thibaut Jacob (thibautquentinjacob@gmail.com>)
 * -----
 * License:
 * MIT License
 * 
 * Copyright (c) 2018 Thibaut Jacob
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies
 * of the Software, and to permit persons to whom the Software is furnished to do
 * so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */





import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import 'rxjs/add/operator/map';
import { HttpClient } from '@angular/common/http';
import { Stock } from '../models/Stock';
import { StockQuote } from '../models/StockQuote';
import { Symbol } from '../models/Symbol';

@Injectable()
export class StockDataService {

    stockData:      Stock[];
    stockDataDay:   Stock[];
    stockQuoteData: StockQuote;

    constructor( private http: HttpClient ) {
        this.stockData      = [];
    }

    /**
     * Request latest quote from the IEX API (cf StockQuote interface)
     * @params symbol: string
     * @return Observable StockQuote
     */
    getStockQuote( symbol: string ): Observable<StockQuote> {
        let stockQuoteData: StockQuote;
        return this.http.get(
            'https://api.iextrading.com/1.0/stock/' + symbol + '/quote'
        ).pipe().map( data => {
            stockQuoteData = {
                price:       data['latestPrice'],
                symbol:      symbol,
                diff:        data['change'],
                diffPercent: data['changePercent'],
                open:        data['open'],
                close:       data['close'],
                high:        data['high'],
                low:         data['low'],
                volume:      data['latestVolume'],
                bid:         data['iexBidPrice'],
                bidSize:     data['iexBidSize'],
                ask:         data['iexAskPrice'],
                askSize:     data['iexAskSize'],
                pe:          data['peRatio'],
            };
            return stockQuoteData;
        });
    }

    /**
     * Pulls intraday stock data from the IEX API.
     * @params stock symbol: string
     * @return Stock data: Observable Stock[]
     */
    getDayStockData( symbol: string ): Observable<Stock[]> {
        const stockDataDay: Stock[] = [];
        return this.http.get(
            'https://api.iextrading.com/1.0/stock/' + symbol + '/chart/1d'
        ).pipe().map( data => {
            // console.log( data );
            for ( let i = 0 ; i < Object.keys( data ).length ; i++ ) {
                if ( data[i].average !== 0 ) {
                    stockDataDay.push({
                        date:    new Date( data[i].date.replace( /(\d{4})(\d{2})(\d{2})/, '$1/$2/$3' ) + ' ' + data[i].minute ),
                        average: data[i].average,
                        high:    data[i].high,
                        low:     data[i].low,
                        volume:  data[i].volume
                    });
                }
            }
            return stockDataDay;
        });
    }

    /**
     * Pulls yearly stock data from the IEX API.
     * @params stock symbol: string
     * @return Stock data: Observable Stock[]
     */
    getStockData( symbol: string ): Observable<Stock[]> {
        console.log( 'Requesting data for symbol ' + symbol );
        const stockData: Stock[] = [];
        return this.http.get(
            'https://api.iextrading.com/1.0/stock/' + symbol + '/chart/1y'
        ).pipe().map( data => {
            // console.log( data );
            for ( let i = 0 ; i < Object.keys( data ).length ; i++ ) {
                stockData.push({
                    date:   new Date( data[i].date.replace( /(\d{4})(\d{2})(\d{2})/, '$1/$2/$3' ) ),
                    open:   data[i].open,
                    high:   data[i].high,
                    low:    data[i].low,
                    close:  data[i].close,
                    volume: data[i].volume
                });
            }
            return stockData;
        });
    }

     /**
     * Computes the SMA (Simple Moving Average) technical indicator on the
     * given period.
     * @params day period: number, stock history value, callback
     * @return void
     */
    computeSMA( dayPeriod: number, stocks, callback ) {
        let sum = 0;
        let firstValueIndex = 0;
        for ( let i = 0 ; i < stocks.length ; i++ ) {
            sum += stocks[i].close;
            if ( i >= dayPeriod - 1 ) {
                stocks[i]['SMA_' + dayPeriod] = Math.round( sum / dayPeriod * 100 ) / 100;
                firstValueIndex = i - dayPeriod + 1;
                sum -= stocks[firstValueIndex].close;
            }
        }
        callback( stocks );
    }

    /**
     * Computes the EMA (Exponential Moving Average) technical indicator on the
     * given period.
     * @params day period: number, stock history value, callback
     * @return void
     */
    computeEMA( dayPeriod: number, stocks, callback ) {
        const weightMultiplier = 2 / ( dayPeriod + 1 );
        this.computeSMA( dayPeriod, stocks, ( SMAStocks ) => {
            for ( let i = 0 ; i < SMAStocks.length ; i++ ) {
                if ( SMAStocks[i]['SMA_' + dayPeriod]) {
                    if ( SMAStocks[i - 1]['SMA_' + dayPeriod]) {
                        SMAStocks[i]['EMA_' + dayPeriod] =
                            Math.round(
                                (( SMAStocks[i].close - SMAStocks[i - 1]['EMA_' + dayPeriod]) *
                                weightMultiplier + SMAStocks[i - 1]['EMA_' + dayPeriod]) * 100
                            ) / 100;
                    } else {
                        SMAStocks[i]['EMA_' + dayPeriod] =
                            Math.round( SMAStocks[i]['SMA_' + dayPeriod] * 100 ) / 100;
                    }
                }
            }
            callback( SMAStocks );
        });
    }

    /**
     * Computes the McGinley technical indicator (cf Investopedia) on the
     * given period.
     * @params day period: number, stock history value, callback
     * @return void
     */
    computeMcGinley( dayPeriod: number, stocks, callback ) {
        let previous = null;
        for ( let i = 0 ; i < stocks.length ; i++ ) {
            if ( i >= dayPeriod - 1 ) {
                if ( !previous ) {
                    stocks[i]['McGinley_' + dayPeriod] = stocks[i].close;
                } else {
                    stocks[i]['McGinley_' + dayPeriod] =
                        Math.round(
                            ( previous + ( stocks[i].close - previous ) /
                            ( dayPeriod / 2 * Math.pow( stocks[i].close / previous, 4 ))
                        ) * 100 ) / 100;
                }
                previous = stocks[i]['McGinley_' + dayPeriod];
            }
        }
        callback( stocks );
    }

    /**
     * Pulls the list of stock symbols supported by the IEX API.
     * @params void
     * @return symbol list: Observable Symbol[]
     */
    getSupportedSymbols(): Observable<Symbol[]> {
        const symbols: Symbol[] = [];
        return this.http.get(
            'https://api.iextrading.com/1.0/ref-data/symbols'
        ).pipe().map( data => {
            for ( let i = 0 ; i < Object.keys( data ).length - 1 ; i++ ) {
                symbols.push({
                    symbol: data[i].symbol,
                    name:   data[i].name
                });
            }
            return symbols;
        });
    }

    /**
     * Pulls dividends from the IEX API.
     * @params stock symbol: string
     * @return dividends: Observable number
     */
    getDividends( symbol: string ): Observable<number> {
        let dividends = 0;
        return this.http.get(
            'https://api.iextrading.com/1.0/stock/' + symbol + '/dividends/1y'
        ).pipe().map( data => {
            if ( data instanceof Array ) {
                data.map( div => {
                    dividends += div.amount;
                });
            }
            return Math.round( dividends * 100 ) / 100;
        });
    }

    /**
     * Pulls key stats (EPS, return on equity. etc.) from the IEX API.
     * @params stock symbol: string
     * @return Key stats: Observable any
     */
    // TODO: Create proper interface to hold key stats information
    getKeyStats( symbol: string ): Observable<any> {
        return this.http.get(
            'https://api.iextrading.com/1.0/stock/' + symbol + '/stats'
        );
    }

    /**
     * Pulls VWAP (Volume Weighted Average Price) from the IEX API.
     * @params stock symbol: string
     * @return VWAP: Observable number
     */
    getVWAP( symbol: string ): Observable<number> {
        return this.http.get(
            'https://api.iextrading.com/1.0/stock/' + symbol + '/chart'
        ).pipe().map( data => {
            if ( data instanceof Array && data.length > 0 ) {
                return data[data.length - 1].vwap;
            }
        });
    }
}



// WEBPACK FOOTER //
// /Users/thibautjacob/Documents/Projects/Portfolio/src/app/services/stock-data.service.ts